import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Terminal, Pause, Play, RefreshCw, Download, Trash2 } from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import { readWithDeadline } from '../../hooks/useDashboardRequests';
import { filterSystemLogs, formatSystemLog, mergeSystemLogs } from '../../services/systemLogStream';
import '../../styles/system-logs.css';

const LOG_TABS = [
  ['all', 'All events'], ['deployment', 'Deployments'], ['http', 'Requests'],
  ['errors', 'Errors'], ['job', 'Background jobs'], ['audit', 'Audit activity'],
];
const matchesTab = (entry, tab) => tab === 'all' || (tab === 'errors'
  ? entry.level === 'error' || entry.level === 'warn' : entry.source === tab);

const megabytes = bytes => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

export default function SystemLogsSection() {
  const [entries, setEntries] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [status, setStatus] = useState('connecting');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [paused, setPaused] = useState(false);
  const [retry, setRetry] = useState(0);
  const [tab, setTab] = useState('all');
  const [polling, setPolling] = useState(false);
  const [lastReceived, setLastReceived] = useState(null);
  const [level, setLevel] = useState('');
  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');
  const [follow, setFollow] = useState(true);
  const [snapshot, setSnapshot] = useState('');
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const cursor = useRef(null);
  const pending = useRef([]);
  const terminal = useRef(null);
  const mounted = useRef(true);
  const snapshotRequest = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; snapshotRequest.current?.abort(); };
  }, []);

  useEffect(() => {
    if (paused) { setStatus('paused'); return undefined; }
    let disposed = false;
    let controller;
    let reconnectTimer;
    let retryDelay = 1000;
    let lastEvent = Date.now();
    let hasEvent = false;
    let mode = 'stream';
    setPolling(false);
    setError('');
    const flush = () => {
      if (!pending.current.length || !mounted.current) return;
      const batch = pending.current;
      pending.current = [];
      setEntries(current => mergeSystemLogs(current, batch));
    };
    const receive = (name, data) => {
      if (disposed) return;
      lastEvent = Date.now();
      hasEvent = true;
      retryDelay = 1000;
      setLastReceived(lastEvent);
      setStatus(mode === 'polling' ? 'polling' : 'live');
      setError('');
      if (name === 'snapshot') {
        if (data.reset) {
          pending.current = [];
          setEntries(data.entries.slice(-1000));
        } else {
          pending.current = mergeSystemLogs(pending.current, data.entries);
        }
        cursor.current = data.cursor;
        if (data.gap) setNotice('Some events are no longer retained, or the backend restarted. Showing recent events.');
      } else if (name === 'log') {
        cursor.current = data.id;
        pending.current = mergeSystemLogs(pending.current, [data]);
      } else if (name === 'metrics') {
        setMetrics(data);
      }
    };
    const denied = err => {
      if (err.status !== 401 && err.status !== 403) return false;
      mode = 'denied';
      setStatus('denied');
      setError(err.message);
      return true;
    };
    const poll = async () => {
      if (disposed) return;
      controller = new AbortController();
      try {
        const data = await readWithDeadline(signal => superAdminApi.getSystemLogSnapshot({
          signal, after: cursor.current,
        }), controller, 'Log refresh', 8000);
        if (disposed) return;
        receive('snapshot', data);
        if (data.metrics) receive('metrics', data.metrics);
        flush();
      } catch (err) {
        if (disposed || denied(err)) return;
        setStatus('offline');
        setError(err.message);
      }
      if (!disposed) reconnectTimer = setTimeout(poll, 3000);
    };
    const startPolling = () => {
      if (disposed || mode !== 'stream') return;
      mode = 'polling';
      controller?.abort();
      clearTimeout(reconnectTimer);
      setPolling(true);
      setStatus('connecting');
      poll();
    };
    const connect = async () => {
      if (disposed) return;
      const attempt = new AbortController();
      controller = attempt;
      lastEvent = Date.now();
      hasEvent = false;
      setStatus(cursor.current ? 'reconnecting' : 'connecting');
      try {
        await superAdminApi.streamSystemLogs({
          signal: attempt.signal,
          after: cursor.current,
          onEvent: (name, data) => {
            if (mode === 'stream' && controller === attempt && !attempt.signal.aborted) receive(name, data);
          },
        });
      } catch (err) {
        if (disposed || mode !== 'stream' || denied(err)) return;
        setError(err.message);
        startPolling();
        return;
      }
      if (disposed || mode !== 'stream') return;
      if (!hasEvent) { startPolling(); return; }
      flush();
      setStatus('reconnecting');
      reconnectTimer = setTimeout(connect, retryDelay);
      retryDelay = Math.min(retryDelay * 2, 15000);
    };
    const flushTimer = setInterval(flush, 200);
    const watchdog = setInterval(() => {
      // A proxy can send HTTP 200 headers but buffer every SSE event indefinitely.
      if (mode === 'stream' && Date.now() - lastEvent >= (hasEvent ? 20000 : 8000)) startPolling();
    }, 1000);
    connect();
    return () => {
      disposed = true;
      clearTimeout(reconnectTimer);
      clearInterval(flushTimer);
      clearInterval(watchdog);
      controller?.abort();
      flush();
    };
  }, [paused, retry]);

  const filtered = useMemo(() => filterSystemLogs(entries.filter(entry => matchesTab(entry, tab)), { level, source, search }), [entries, tab, level, source, search]);
  useEffect(() => {
    if (follow && terminal.current) terminal.current.scrollTop = terminal.current.scrollHeight;
  }, [filtered, follow]);

  const exportLogs = () => {
    const blob = new Blob([filtered.map(formatSystemLog).join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `system-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const loadSnapshot = async () => {
    snapshotRequest.current?.abort();
    snapshotRequest.current = new AbortController();
    setLoadingSnapshot(true);
    try {
      const data = await readWithDeadline(signal => superAdminApi.getServerLogs(300, { signal }), snapshotRequest.current, 'PM2 snapshot', 12000);
      if (mounted.current) setSnapshot(data.logs || 'No PM2 logs available.');
    } catch (err) {
      if (mounted.current) setSnapshot(`Unable to load PM2 snapshot: ${err.message}`);
    } finally {
      if (mounted.current) setLoadingSnapshot(false);
    }
  };

  return (
    <section className="sa-section fade-in sa-system-logs">
      <div className="sa-section-header sl-heading">
        <div>
          <h2><Terminal size={24} /> Realtime Logs</h2>
          <p className="subtitle">Requests, deployment progress, build output, background jobs, and system usage.</p>
        </div>
        <span className={`sl-status sl-status-${status}`} role="status">● {status === 'polling' ? 'Auto-refresh · 3s' : status === 'live' ? 'Live stream' : status}</span>
      </div>

      <div className="sl-metrics" aria-label="Backend usage metrics">
        {[
          ['Process CPU', metrics ? `${metrics.processCpuPercent}%` : '—', '100% = one CPU core'],
          ['Process memory', metrics ? megabytes(metrics.processRssBytes) : '—', metrics ? `Heap ${megabytes(metrics.heapUsedBytes)}` : 'Waiting for metrics'],
          ['Host memory', metrics ? `${(metrics.hostUsedMemoryBytes / metrics.hostTotalMemoryBytes * 100).toFixed(1)}%` : '—', metrics ? `Load (1m) ${metrics.hostLoad1m.toFixed(2)} · ${metrics.cpuCount} cores` : 'Waiting for metrics'],
          ['Requests / second', metrics ? metrics.requests.perSecond : '—', metrics ? `${metrics.requests.active} active · ${metrics.requests.averageDurationMs} ms avg` : 'Waiting for metrics'],
          ['HTTP failures', metrics ? `${metrics.requests.serverErrors} server / ${metrics.requests.clientErrors} client` : '—', metrics ? `${metrics.requests.total} requests · ${metrics.requests.aborted} aborted since startup` : 'Waiting for metrics'],
        ].map(([label, value, detail]) => (
          <div className="sa-card sl-metric" key={label}><span>{label}</span><strong>{value}</strong><small>{detail}</small></div>
        ))}
      </div>
      <p className="sl-caption">Current backend process only · latest 1,000 events retained in memory · history resets on restart.
        {metrics && ` PID ${metrics.pid} · metrics ${new Date(metrics.timestamp).toLocaleTimeString()}${!['live', 'polling'].includes(status) ? ' (not live)' : ''}`}
      </p>
      {polling && !paused && status !== 'denied' && <p className="sl-caption">Streaming is unavailable. Events refresh automatically every 3 seconds. Use Reconnect to try streaming again.</p>}
      {error && <div className="sl-alert" role="alert">{error}</div>}
      {notice && <div className="sl-alert" role="status">{notice} <button className="sa-btn sa-btn-outline" onClick={() => setNotice('')}>Dismiss</button></div>}

      <div className="sl-tabs" role="tablist" aria-label="Log categories">
        {LOG_TABS.map(([id, label], index) => <button key={id} id={`log-tab-${id}`} type="button" role="tab"
          aria-selected={tab === id} aria-controls="log-events-panel" tabIndex={tab === id ? 0 : -1}
          onClick={() => { setTab(id); setSource(''); setLevel(''); }}
          onKeyDown={event => {
            const next = event.key === 'ArrowRight' ? (index + 1) % LOG_TABS.length
              : event.key === 'ArrowLeft' ? (index + LOG_TABS.length - 1) % LOG_TABS.length
                : event.key === 'Home' ? 0 : event.key === 'End' ? LOG_TABS.length - 1 : null;
            if (next === null) return;
            event.preventDefault();
            setTab(LOG_TABS[next][0]); setSource(''); setLevel('');
            document.getElementById(`log-tab-${LOG_TABS[next][0]}`)?.focus();
          }}>{label}<span>{entries.filter(entry => matchesTab(entry, id)).length}</span></button>)}
      </div>
      <div className="sl-toolbar">
        <input className="sa-input sl-search" aria-label="Search system logs" placeholder="Search app, owner, deployment ID, job, or message…" value={search} onChange={event => setSearch(event.target.value)} />
        <select className="sa-input" aria-label="Log severity" value={level} onChange={event => setLevel(event.target.value)}>
          <option value="">All levels</option>{['debug', 'info', 'warn', 'error'].map(value => <option key={value} value={value}>{value.toUpperCase()}</option>)}
        </select>
        <select className="sa-input" aria-label="Log source" value={source} onChange={event => setSource(event.target.value)}>
          <option value="">All sources</option><option value="deployment">Deployments / builds</option><option value="http">HTTP requests</option><option value="job">Background jobs</option><option value="audit">Audit activity</option><option value="application">Application</option><option value="runtime">Runtime</option>
        </select>
        <button className="sa-btn sa-btn-outline" onClick={() => setPaused(value => !value)}>{paused ? <Play size={15} /> : <Pause size={15} />}{paused ? 'Resume' : 'Pause'}</button>
        <button className="sa-btn sa-btn-outline" onClick={() => { setPaused(false); setRetry(value => value + 1); }}><RefreshCw size={15} />Reconnect</button>
        <button className="sa-btn sa-btn-outline" onClick={() => { pending.current = []; setEntries([]); setNotice('View cleared. New events will appear as they arrive.'); }}><Trash2 size={15} />Clear view</button>
        <button className="sa-btn sa-btn-outline" onClick={exportLogs} disabled={!filtered.length}><Download size={15} />Export visible</button>
      </div>
      <div className="sl-view-meta">
        <span>{filtered.length} visible / {entries.length} buffered events{lastReceived && ` · Last received ${new Date(lastReceived).toLocaleTimeString()}`}</span>
        <label><input type="checkbox" checked={follow} onChange={event => setFollow(event.target.checked)} /> Auto-scroll</label>
      </div>
      <div className="sl-terminal" id="log-events-panel" role="tabpanel" aria-labelledby={`log-tab-${tab}`} ref={terminal} tabIndex={0} aria-label="System log entries" onScroll={() => {
        const element = terminal.current;
        if (element && element.scrollHeight - element.scrollTop - element.clientHeight > 50) setFollow(false);
      }}>
        {!filtered.length && <p className="sl-empty">{paused ? 'Stream paused. Resume to receive events.' : status === 'denied' ? 'Access denied. Sign in again with a superadmin account.' : status === 'offline' ? 'Logs are unavailable. Check the connection error above or select Reconnect.' : entries.length ? 'No events match these filters.' : ['live', 'polling'].includes(status) ? 'Connected. No recent events. New requests and deployment progress will appear here automatically.' : 'Connecting to backend logs…'}</p>}
        {filtered.map(entry => <div className={`sl-entry sl-entry-${entry.level}`} key={entry.id}>
          <time dateTime={entry.timestamp} title={entry.timestamp}>{new Date(entry.timestamp).toLocaleTimeString()}</time>
          <span className="sl-level">{entry.level}</span>
          <span className="sl-source">{entry.source}</span>
          <div className="sl-message">{entry.message}
            {(entry.appName || entry.owner || entry.phase || entry.actor) && <small className="sl-request">
              {[entry.appName && `App: ${entry.appName}`, entry.owner && `Owner: ${entry.owner}`, entry.deploymentType && `Type: ${entry.deploymentType}`, entry.phase && `Stage: ${entry.phase}`, entry.actor && `Actor: ${entry.actor}`].filter(Boolean).join(' · ')}
            </small>}
            {entry.deploymentId && <small className="sl-request">Deployment {entry.deploymentId}</small>}
            {entry.jobId && <small className="sl-request">Job {entry.job} · {entry.jobId}</small>}
            {entry.requestId && <small className="sl-request">Request {entry.requestId}</small>}
          </div>
        </div>)}
      </div>
      <details className="sl-pm2">
        <summary>PM2 process logs (snapshot)</summary>
        <p className="sl-caption">Optional output from PM2-managed processes. Refresh manually; availability depends on the server setup.</p>
        <button className="sa-btn sa-btn-outline" onClick={loadSnapshot} disabled={loadingSnapshot}><RefreshCw size={15} />{loadingSnapshot ? 'Loading…' : 'Load PM2 snapshot'}</button>
        {snapshot && <pre className="sl-terminal">{snapshot}</pre>}
      </details>
    </section>
  );
}
